import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #stockInitializationController;

  async connectedCallback() {
    const metadataElement = this.querySelector('[data-stock-priority-config]');
    if (metadataElement && !this.hasAttribute('data-stock-priority-ready')) {
      try {
        const metadata = JSON.parse(metadataElement.textContent || '{}');
        const selected = new Set(new URL(window.location.href).searchParams.getAll('filter.v.availability').filter((value) => value === '0' || value === '1'));
        if (selected.size === 1 || metadata.total === 0 || (this.dataset.stockPriority === 'collection' && (metadata.available === 0 || metadata.available === metadata.total))) {
          this.dataset.stockPriorityReady = 'ready';
          this.removeAttribute('aria-busy');
        }
      } catch {}
    }
    if (this.hasAttribute('data-stock-priority') && !this.hasAttribute('data-stock-priority-ready')) {
      const controller = new AbortController();
      this.#stockInitializationController = controller;
      const abortOnTimeout = () => controller.abort();
      this.addEventListener('stock-priority-timeout', abortOnTimeout, { once: true });
      try {
        const requestedURL = window.location.href;
        const html = await sectionRenderer.getSectionHTML(this.getAttribute('section-id'), false, new URL(requestedURL), controller.signal);
        if (!this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('results-list[data-stock-priority]');
        const nextGrid = source?.querySelector('[ref="grid"]');
        const grid = this.querySelector('[ref="grid"]');
        if (!controller.signal.aborted && this.dataset.stockPriorityReady !== 'fallback') {
          if (source && grid && nextGrid && window.location.href === requestedURL) grid.replaceChildren(...nextGrid.childNodes);
          this.dataset.stockPriorityReady = source?.dataset.stockPriorityReady || 'fallback';
        }
      } catch (error) {
        console.warn('Stock-priority initialization failed; retaining native results.', error);
        this.dataset.stockPriorityReady = 'fallback';
      } finally {
        this.removeEventListener('stock-priority-timeout', abortOnTimeout);
        if (this.#stockInitializationController === controller) this.#stockInitializationController = undefined;
        this.removeAttribute('data-stock-priority-pending');
      }
      this.removeAttribute('aria-busy');
    }
    if (!this.isConnected) return;
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#stockInitializationController?.abort();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

